import { useState, useEffect } from "react";
import { NavLink, useLocation, Link } from "react-router-dom";
import { Menu, X, ArrowRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { EnprexLogo } from "./Logo";

const NAV = [
  { to: "/", label: "Home", testid: "nav-home-link" },
  { to: "/about", label: "About", testid: "nav-about-link" },
  { to: "/industries", label: "Industries", testid: "nav-industries-link" },
  { to: "/services", label: "Services", testid: "nav-services-link" },
  { to: "/contact", label: "Contact", testid: "nav-contact-link" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-[#050A14]/92 backdrop-blur-2xl border-b border-white/5"
          : "bg-transparent"
      }`}
    >
      <div className="container-x flex items-center justify-between h-24">
        <EnprexLogo size={36} />

        <nav className="hidden xl:flex items-center gap-7">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={item.testid}
              className={({ isActive }) =>
                `text-[13px] font-medium tracking-wide transition-colors ${
                  isActive ? "text-white border-b border-[#D4AF37] pb-1" : "text-slate-400 hover:text-white"
                }`
              }
              end={item.to === "/"}
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Link
            to="/contact"
            data-testid="header-cta-contact"
            className="hidden md:inline-flex items-center gap-2 bg-[#D4AF37] text-[#0B1F3A] px-5 py-2.5 text-[11px] font-semibold uppercase tracking-[0.18em] hover:bg-[#FDE047] transition-colors"
          >
            Contact Us <ArrowRight size={14} />
          </Link>
          <Link
            to="/contact#partner"
            data-testid="header-cta-partner"
            className="hidden lg:inline-flex items-center gap-2 border border-white/20 text-slate-300 px-5 py-2.5 text-[11px] font-semibold uppercase tracking-[0.18em] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
          >
            Partner with us
          </Link>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="xl:hidden text-white p-2"
            aria-label="Toggle menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="xl:hidden bg-[#050A14]/98 backdrop-blur-2xl border-t border-white/5 overflow-hidden"
          >
            <div className="container-x py-6 flex flex-col gap-4">
              {NAV.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  data-testid={`mobile-${item.testid}`}
                  className={({ isActive }) =>
                    `text-base font-medium py-2 border-b border-white/5 ${
                      isActive ? "text-white" : "text-slate-400"
                    }`
                  }
                  end={item.to === "/"}
                >
                  {item.label}
                </NavLink>
              ))}
              <Link
                to="/contact"
                data-testid="mobile-cta-contact"
                className="bg-[#D4AF37] text-[#0B1F3A] px-5 py-3 text-sm font-semibold uppercase tracking-[0.15em] text-center mt-2"
              >
                Contact Us
              </Link>
              <Link
                to="/contact#partner"
                data-testid="mobile-cta-partner"
                className="border border-white/20 text-slate-200 px-5 py-3 text-sm font-semibold uppercase tracking-[0.15em] text-center"
              >
                Partner with us
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
